// Aktionsmodul: automatische Antwort an den Absender (Mieter/externer Absender).
// Nie bei rechtlich sensiblen Tickets. Nutzt — falls vorhanden — eine passende
// email_vorlagen-Zeile (per waehleVorlage), sonst eine neutrale Eingangsbestätigung.
import { supabase } from '../../supabase/client';
import { ersetzePlatzhalter, htmlZuText } from '../platzhalter';
import type { AktionEntwurf, AktionKontext, AktionModul, TicketAktion } from '../types';
import { ergaenzeRueckfragen } from '../../vorgaenge/start';

type Vorlage = NonNullable<AktionKontext['stammdaten']>['vorlagen'][number];
type TicketBasis = AktionKontext['ticket'];

/**
 * Rein & testbar: wählt eine passende E-Mail-Vorlage anhand des ki_trigger.
 * Reihenfolge: themen-spezifische Vorlage (v1: 'nebenkosten_statusanfrage' bei
 * Nebenkosten-Stichwörtern) → generische Eingangsbestätigung ('antwort_absender',
 * im Vorlagen-Tab zuordenbar) → null (Modul-Fallback-Text).
 */
export function waehleVorlage(vorlagen: Vorlage[], ticket: TicketBasis, vorgangVorlageId?: string | null): Vorlage | null {
  // #489: Antwortvorlage der Vorgangsart hat Vorrang
  const vorgang = vorgangVorlageId ? vorlagen.find((v) => v.id === vorgangVorlageId) : undefined;
  if (vorgang) return vorgang;
  const haystack = `${ticket.erkannte_aufgabe ?? ''} ${ticket.titel ?? ''}`.toLowerCase();
  const stichworte = ['nebenkosten', 'abrechnung', 'betriebskosten'];
  if (stichworte.some((s) => haystack.includes(s))) {
    const spezifisch = vorlagen.find((v) => v.ki_trigger === 'nebenkosten_statusanfrage');
    if (spezifisch) return spezifisch;
  }
  return vorlagen.find((v) => v.ki_trigger === 'antwort_absender') ?? null;
}

const NEUTRALE_BESTAETIGUNG =
  'Guten Tag,\n\nvielen Dank für Ihre Nachricht. Ihr Anliegen ist bei uns eingegangen und wird bearbeitet. Wir melden uns, sobald es Neuigkeiten gibt.\n\nMit freundlichen Grüßen';

const NEUTRALE_BESTAETIGUNG_MIT_NUMMER = (ticketNummer: string) =>
  `Guten Tag,\n\nvielen Dank für Ihre Nachricht. Ihr Anliegen ist bei uns eingegangen und wird unter der Vorgangsnummer ${ticketNummer} bearbeitet. Wir melden uns, sobald es Neuigkeiten gibt.\n\nMit freundlichen Grüßen`;

export function erzeugen(ctx: AktionKontext): AktionEntwurf | null {
  if (ctx.ticket.ist_rechtlich_sensibel) return null;

  const empfaenger = ctx.stammdaten?.mieter?.email ?? ctx.stammdaten?.absenderEmail ?? null;
  if (!empfaenger) return null;

  const vorlagen = ctx.stammdaten?.vorlagen ?? [];
  const platzhalter = ctx.stammdaten?.platzhalter ?? {};
  const vorlage = waehleVorlage(vorlagen, ctx.ticket, ctx.vorgang?.antwortVorlageId);

  let betreff: string;
  let text: string;
  let payload: Record<string, unknown> = {};

  if (vorlage) {
    // Zusätzlich zu den Stammdaten-Platzhaltern: Ticket-Bezüge für die Vorlage.
    const werte = {
      ...platzhalter,
      'ticket.nummer': ctx.ticket.ticket_nummer ?? '',
      'ticket.titel': ctx.ticket.titel ?? '',
    };
    betreff = ersetzePlatzhalter(vorlage.betreff_template ?? '', werte);
    text = htmlZuText(ersetzePlatzhalter(vorlage.html_template ?? '', werte));
    payload = { vorlage_id: vorlage.id };
  } else {
    betreff = `AW: ${ctx.ticket.titel}`;
    const absenderName = platzhalter['absender.name'] ?? '';
    const bestaetigung = ctx.ticket.ticket_nummer
      ? NEUTRALE_BESTAETIGUNG_MIT_NUMMER(ctx.ticket.ticket_nummer)
      : NEUTRALE_BESTAETIGUNG;
    text = absenderName ? `${bestaetigung}\n${absenderName}` : bestaetigung;
  }

  // #489: fehlende Angaben des Vorgangs vor dem Gruß nachfragen
  text = ergaenzeRueckfragen(text, ctx.vorgang?.rueckfragen ?? []);

  return {
    typ: 'antwort_absender',
    freigabe_erforderlich: true,
    ist_rechtlich_sensibel: false,
    empfaenger,
    empfaenger_kontakt_id: null,
    betreff,
    entwurf_text: text,
    payload,
    ki_confidence: ctx.ticket.ki_confidence ?? null,
  };
}

// Nachbereitung nach Mail-Versand (dünner Supabase-Wrapper, Konvention: nicht unit-getestet).
export async function nachVersand(aktion: TicketAktion, benutzerId: string | null, opts?: { emailVerknuepft?: boolean }): Promise<void> {
  await supabase.from('ticket_historie').insert({
    ticket_id: aktion.ticket_id,
    benutzer_id: benutzerId,
    aktion: 'antwort_gesendet',
    neuer_wert: `Antwort an ${aktion.empfaenger} gesendet`,
  });
  // Mail hängt als Thread-Eintrag am Ticket → kein Volltext-Spiegel-Kommentar mehr nötig.
  if (opts?.emailVerknuepft) return;
  await supabase.from('ticket_kommentare').insert({
    ticket_id: aktion.ticket_id,
    autor_id: benutzerId,
    inhalt: `Antwort gesendet:\n\n${aktion.entwurf_text}`,
    ist_intern: true,
  });
}

export const antwortAbsenderModul: AktionModul = { typ: 'antwort_absender', erzeugen, nachVersand };
